/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useLanguage } from '@/providers/LanguageProvider';
import { request } from '@/services/request';
import { iconMap } from '@/ux-common/src/lib/helper';
import { toast } from 'sonner';
import { AppGraph, Component } from 'types/api-data';
import { IAppData, ICleanedAppData, IComponentData } from 'types/new-app-d';

import { useSearch } from '@/hooks/useSearch';

import { CustomButton, SearchInput } from '../common';
import { System } from '../icons';
import { AssignableComp } from '../new-app-page/components/AssignableComp';
import { FullScreenSpinner } from '../spinner/full-screen-spinner';

function cleanedData(obj: ICleanedAppData) {
  const { components, description, display_name, owner, sla } = obj;
  return { components, description, display_name, owner, sla };
}

const dataTransform = (data: AppGraph) => {
  const componentsData = data.components.filter(
    (item: Component) => item.domain === 'logical'
  );

  return componentsData ?? [];
};

export const ComponentDisplayItems = ({
  data,
  onRefresh,
  appDetails,
  addedComponent,
  setAddedComponent,
  setAppDetails,
}: {
  data?: AppGraph;
  onRefresh: () => void;
  appDetails: IAppData;
  addedComponent: IComponentData[];
  setAddedComponent: React.Dispatch<React.SetStateAction<IComponentData[]>>;
  setAppDetails: (value: IAppData) => void;
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  // const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { messages } = useLanguage();
  const langData = messages?.ApplicationDetailPage;

  // TIP: Custom hook for searching functionality.
  const { searchTerm, filteredItems, handlerSearchChange, handleClearSearch } =
    useSearch(data!, 'logical');

  const handleAddComponent = async (selectedComponent: IComponentData[]) => {
    try {
      const logicalComponents = dataTransform(appDetails);
      setIsLoading(true);

      const body = {
        ...cleanedData(appDetails),
        components: [...logicalComponents, ...selectedComponent],
      };

      const response = await request.put(
        { endpoint: `apps/${data?.name}` },
        body
      );

      setAppDetails(response[0]);
      toast.success(langData?.component_added_successfully);
      onRefresh();
      return response[0];
    } catch (error) {
      toast.error(langData?.error_in_adding_component);
      console.log('error', error);
      return error;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full">
      {isLoading && <FullScreenSpinner text="Adding Component" />}
      <div className="mb-[11px] flex items-center justify-between">
        <h3 className="text-h2 text-body-primary">
          {messages?.General?.components}
        </h3>

        {data && dataTransform(data)?.length > 10 ? (
          <SearchInput
            placeholder={messages?.General?.search_by_name}
            value={searchTerm}
            onChange={(e) => handlerSearchChange(e.target.value)}
            onClear={handleClearSearch}
          />
        ) : null}
      </div>
      <div className="max-h-96 overflow-y-auto rounded-lg border border-line-secondary bg-canvas-background-primary px-2 py-3">
        {data && dataTransform(data)?.length > 0 ? (
          <>
            {filteredItems.length > 0 ? (
              <>
                <div className="text-body-primary">
                  {filteredItems.map((item: Component) => (
                    <div
                      className="flex items-center gap-2.5 p-2.5"
                      key={item.display_name}
                    >
                      <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-white text-black">
                        <Image
                          className="size-5"
                          src={iconMap[item.type] ?? iconMap['default']}
                          alt="Logo"
                          width={100}
                          height={100}
                        />
                      </span>
                      <div className="flex flex-col">
                        {item.type?.startsWith('workflow.') ? (
                          <Link
                            href={`/components/${item.component_name}`}
                            className="group flex flex-col"
                          >
                            <span className="text-body-sm font-normal text-form-input-disabled ">
                              {item.type}
                            </span>
                            <h4 className="text-h4 font-medium group-hover:underline group-hover:underline-offset-2">
                              {item.display_name}
                            </h4>
                          </Link>
                        ) : (
                          <>
                            <Link
                              href={{
                                pathname: '/components',
                                query: {
                                  search: encodeURIComponent(item.display_name),
                                },
                              }}
                              className="group"
                            >
                              <span className="text-body-sm font-normal text-form-input-disabled">
                                {item.type}
                              </span>
                              <h4 className="text-h4 font-medium text-body-subtle group-hover:underline group-hover:underline-offset-2">
                                {item.display_name}
                              </h4>
                            </Link>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {/* TODO: border-white -> okahu color */}
                <div className="p-2.5">
                  <CustomButton
                    customSize="regular"
                    customVariant="secondary"
                    onClick={() => setDialogOpen(true)}
                  >
                    {messages?.General?.associate_component}
                  </CustomButton>
                </div>
              </>
            ) : (
              // No matches found message
              <div className="flex flex-col items-center p-4 text-white">
                {System.InfoIcon('size-9 text-form-input-disabled')}
                <p className="mb-3 mt-1 text-center text-xs">
                  No components found
                </p>
                <CustomButton
                  type="button"
                  customSize="regular"
                  customVariant="secondary"
                  onClick={() => setDialogOpen(true)}
                >
                  {messages?.General?.associate_component}
                </CustomButton>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center rounded-md p-4 text-white">
            {System.InfoIcon('size-6 text-form-input-disabled')}
            <p className="mb-3 mt-1 text-center text-xs">
              {searchTerm
                ? `No components found.`
                : langData?.you_dont_have_any_components_added}
            </p>
            <CustomButton
              type="button"
              customSize="regular"
              customVariant="secondary"
              onClick={() => setDialogOpen(true)}
            >
              {messages?.General?.associate_component}
            </CustomButton>
          </div>
        )}
      </div>
      {dialogOpen && (
        <AssignableComp
          open={dialogOpen}
          setOpen={setDialogOpen}
          addedComponent={addedComponent}
          setAddedComponent={setAddedComponent}
          addComponentFromDetail={handleAddComponent}
        />
      )}
    </div>
  );
};
